"use client";

import { useState } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Box,
  Tooltip,
  Divider,
  Collapse,
} from "@mui/material";

import Link from "next/link";
import { usePathname } from "next/navigation";
import BrandLogo from "../brand-logo";

import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ApartmentIcon from "@mui/icons-material/Apartment";
import DashboardIcon from "@mui/icons-material/Dashboard";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import SecurityIcon from "@mui/icons-material/Security";
import LinkIcon from "@mui/icons-material/Link";

import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const drawerWidth = 260;
const collapsedWidth = 72;

//Menu links and icons
const menu = [
  { text: "Dashboard", icon: <DashboardIcon />, href: "/" },
  { text: "Appfolio", icon: <HomeWorkIcon />, href: "/appfolio" },
  { text: "Property Management", icon: <ApartmentIcon />, href: "/property-management" },
  { text: "Property Mapping", icon: <LocationCityIcon />, href: "/property-mapping-manage" },
  { text: "Account Mapping", icon: <LinkIcon />, href: "/account-mapping-manage" },
  { text: "Bank Mapping", icon: <AccountBalanceIcon />, href: "/bank-account-mapping" },
  {
    text: "System Access",
    icon: <SecurityIcon />,
    permission: "admin",
    href: "/system-access/admin",
    children: [
      { text: "Admin", icon: <SettingsApplicationsIcon />, href: "/system-access/admin" },
      { text: "Roles", icon: <ManageAccountsIcon />, href: "/system-access/roles" },
    ],
  },
];

export default function Sidebar({
  mobileOpen = false,
  onClose,
  desktopOpen = true,
  currentUserRole = "admin",
}) {

  const pathname = usePathname();
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"), {
    noSsr: true,
  });

  const [expandedMenus, setExpandedMenus] = useState({});

  const handleToggle = (text) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [text]: !prev[text],
    }));

  };

  const isPathActive = (href) => {
    if (!href) return false;

    // Exact match
    if (pathname === href) return true;

    // Dynamic child match: "/parent/data"
    return pathname.startsWith(href + "/");
  };

  const drawerContent = (
    <Box>
      <BrandLogo desktopOpen={desktopOpen} />

      {/* <Divider sx={{ mt: 1 }} /> */}

      <List sx={{ mt: 1 }}>

        {menu.map((item) => {

          // ---- ADMIN PERMISSION CHECK ----
          if (item.permission && item.permission !== currentUserRole) {
            return null;
          }

          const parentActive =
            item.children?.some((child) => isPathActive(child.href));

          const isActive = parentActive;


          // ---- SUB MENU RENDERING ----
          if (item.children) {
            return (
              <Box key={item.text}>

                <Tooltip title={!desktopOpen ? item.text : ""} placement="right">

                  <ListItemButton
                    onClick={(e) => {
                      // e.preventDefault();      // 👈 Prevent any navigation
                      handleToggle(item.text); // 👈 Only toggle dropdown
                    }}
                    component={Link}
                    href={item.href}
                    sx={{
                      mx: 1,
                      my: 0.5,
                      borderRadius: 2,
                      justifyContent: desktopOpen ? "initial" : "center",
                      px: desktopOpen ? 2 : 1.5,
                      backgroundColor: isActive ? "#E2E8F0" : "transparent",
                      "&:hover": { backgroundColor: "#E2E8F0" },
                    }}
                  >

                    <ListItemIcon
                      sx={{
                        color: isActive ? "#1D4ED8" : "#0F172A",
                        minWidth: 40,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>

                    <ListItemText
                      primary={item.text}
                      sx={{
                        opacity: desktopOpen ? 1 : 0,
                        transition: "opacity 0.2s ease",
                        whiteSpace: "nowrap",
                        color: isActive ? "#1D4ED8" : "#0F172A",
                      }}
                    />

                    {/* ---- ARROW NEXT TO TITLE ---- */}
                    {desktopOpen && (
                      <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
                        {expandedMenus[item.text] ? (
                          <ExpandMoreIcon fontSize="small" />
                        ) : (
                          <ChevronRightIcon fontSize="small" />
                        )}
                      </Box>
                    )}

                  </ListItemButton>
                </Tooltip>

                {/* ---- CHILDREN MENU ITEMS ---- */}
                <Collapse
                  in={expandedMenus[item.text]}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box
                    sx={{
                      pl: desktopOpen ? 4 : 0,
                    }}
                  >
                    {item.children.map((child) => {

                      const childActive = pathname === child.href || pathname.includes(child.href);

                      return (
                        <Tooltip key={child.text} title={!desktopOpen ? child.text : ""} placement="right">

                          <ListItemButton
                            component={Link}
                            href={child.href}
                            onClick={isMobile ? onClose : undefined}
                            sx={{
                              mx: 1,
                              my: 0.5,
                              borderRadius: 2,
                              justifyContent: desktopOpen ? "initial" : "center",
                              px: desktopOpen ? 2 : 1.5,
                              backgroundColor: childActive ? "#E2E8F0" : "transparent",
                              "&:hover": { backgroundColor: "#E2E8F0" },
                            }}
                          >

                            <ListItemIcon
                              sx={{
                                color: childActive ? "#1D4ED8" : "#0F172A",
                                minWidth: 40,
                              }}
                            >
                              {child.icon}
                            </ListItemIcon>

                            <ListItemText
                              primary={child.text}
                              sx={{
                                opacity: desktopOpen ? 1 : 0,
                                whiteSpace: "nowrap",
                                color: childActive ? "#1D4ED8" : "#0F172A",
                              }}
                            />

                          </ListItemButton>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Collapse>

              </Box>
            );
          }

          // ---- NORMAL MENU ITEM RENDERING ----
          return (
            <Tooltip key={item.text} title={!desktopOpen ? item.text : ""} placement="right">

              <ListItemButton
                component={Link}
                href={item.href}
                onClick={() => {
                  setExpandedMenus({});
                  isMobile && onClose?.();
                }}
                sx={{
                  mx: 1,
                  my: 0.5,
                  borderRadius: 2,
                  justifyContent: desktopOpen ? "initial" : "center",
                  px: desktopOpen ? 2 : 1.5,
                  backgroundColor: isPathActive(item.href) ? "#E2E8F0" : "transparent",
                  "&:hover": { backgroundColor: "#E2E8F0" },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isPathActive(item.href) ? "#1D4ED8" : "#0F172A",
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>

                <ListItemText
                  primary={item.text}
                  sx={{
                    opacity: desktopOpen ? 1 : 0,
                    whiteSpace: "nowrap",
                    color: isPathActive(item.href) ? "#1D4ED8" : "#0F172A",
                  }}
                />

              </ListItemButton>
            </Tooltip>
          );
        })}

      </List>
    </Box>
  );

  return (
    <>

      {/* MOBILE DRAWER */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              backgroundColor: "#F8FAFC",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* DESKTOP DRAWER */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          open
          sx={{
            width: desktopOpen ? drawerWidth : collapsedWidth,
            flexShrink: 0,
            whiteSpace: "nowrap",
            "& .MuiDrawer-paper": {
              width: desktopOpen ? drawerWidth : collapsedWidth,
              overflowX: "hidden",
              transition: theme.transitions.create("width"),
              borderRight: "1px solid #E5E7EB",
              backgroundColor: "#F8FAFC",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

    </>
  );
}
